<!--
  Moderation Dashboard - Content Type Implementation Status
  
  Currently Implemented (Frontend Ready):
  - questions: Forum questions
  - answers: Forum answers  
  - posts: User posts
  - comments: Comments on content
  
  Backend Ready (Frontend Pending):
  - articles, blogs, challenges, chats, products, links
  - organizations, communities, events, ideas, jobs
  - work_items, tenders, media, entities, people, polls
  
  TODO: Uncomment content types in contentTypeOptions, calculateContentTypeStats, 
  and viewContent methods when frontend implementation is ready.
-->
<template>
  <div class="moderation-dashboard">
    <!-- Header - Full Width -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between align-items-center header-row">
        <div>
          <h2 class="mb-1">{{ $t('backoffice.moderation.dashboard-title') || 'Panel de Moderación' }}</h2>
          <p class="text-muted mb-0">
            {{ $t('backoffice.moderation.dashboard-subtitle') || 'Revisar y moderar respuestas del foro y comentarios pendientes de aprobación' }}
          </p>
        </div>
        <div class="d-flex align-items-center header-badges">
          <b-badge variant="warning" class="mr-2">
            {{ moderationStats.pending || 0 }} {{ $t('backoffice.moderation.pending-items') || 'Elementos Pendientes' }}
          </b-badge>
          <b-badge variant="success" class="mr-2">
            {{ moderationStats.approved || 0 }} {{ $t('backoffice.moderation.approved-today') || 'Aprobados Hoy' }}
          </b-badge>
          <b-badge variant="danger">
            {{ moderationStats.rejected || 0 }} {{ $t('backoffice.moderation.rejected-today') || 'Rechazados Hoy' }}
          </b-badge>
        </div>
      </div>
    </b-card>

    <!-- Statistics Cards -->
    <div class="row mb-4 stats-cards">
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="ClockIcon" size="24" class="text-warning mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.pending || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.pending') || 'Pendiente' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="CheckCircleIcon" size="24" class="text-success mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.approved || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.approved') || 'Aprobado' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="XCircleIcon" size="24" class="text-danger mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.rejected || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.rejected') || 'Rechazado' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="UsersIcon" size="24" class="text-info mr-2" />
            <div>
              <h4 class="mb-0">{{ activeModerators || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.active-moderators') || 'Moderadores Activos' }}</small>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">{{ $t('action.loading') || 'Cargando...' }}</p>
    </div>

    <!-- Content when loaded -->
    <div v-else class="row">
      <div class="col-lg-3 col-md-2 mb-2">
        <div class="moderation-sidebar">
          <div class="moderation-sidebar-scroll">
        <!-- Filters Card -->
        <b-card class="mb-1 moderation-sidebar-section">
          <div class="d-flex justify-content-between align-items-center ">
            <h5 class="mb-0">{{ $t('backoffice.moderation.filters') || 'Filtros' }}</h5>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="clearFilters"
            >
              {{ $t('backoffice.moderation.clear-filters') || 'Limpiar' }}
            </b-button>
          </div>
          
          <!-- Search -->
          <div class="mb-1">
            <label class="form-label">{{ $t('backoffice.moderation.search') || 'Buscar' }}</label>
            <b-input-group class="moderation-search-group">
              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('backoffice.moderation.search-placeholder') || 'Buscar contenido...'"
                @input="onSearchInput"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary" @click="clearSearch">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <!-- Content Type -->
          <div class="mb-1">
            <label class="form-label">{{ $t('backoffice.moderation.content-type') || 'Tipo de Contenido' }}</label>
            <b-form-select
              v-model="filters.contentType"
              :options="contentTypeOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Moderation Status -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.status') || 'Estado' }}</label>
            <b-form-select
              v-model="filters.moderationStatus"
              :options="statusOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Forum -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.forum') || 'Foro' }}</label>
            <b-form-select
              v-model="filters.forumKey"
              :options="forumOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Moderator -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.select-moderator') || 'Seleccionar Moderador' }}</label>
            <b-form-select
              v-model="filters.moderatedBy"
              :options="moderatorOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Date Range -->
          <div class="mb-2">
            <label class="form-label">{{ $t('backoffice.moderation.date-range') || 'Rango de Fechas' }}</label>
            <b-form-datepicker
              v-model="filters.dateFrom"
              :placeholder="$t('backoffice.moderation.from-date') || 'Fecha Desde'"
              @input="applyFilters"
              class="mb-2"
            />
            <b-form-datepicker
              v-model="filters.dateTo"
              :placeholder="$t('backoffice.moderation.to-date') || 'Fecha Hasta'"
              @input="applyFilters"
            />
          </div>

          <!-- Actions -->
          <div class="d-flex gap-2">
            <b-button
              variant="outline-primary"
              @click="refreshData"
              :disabled="isLoading"
              block
            >
              <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
              {{ $t('action.refresh') || 'Actualizar' }}
            </b-button>
          </div>
        </b-card>

        <b-card class="mb-1 moderation-sidebar-section">
          <div
            class="sidebar-section-header d-flex justify-content-between align-items-center"
            @click="isPerformanceOpen = !isPerformanceOpen"
          >
            <h5 class="mb-0">
              {{ $t('backoffice.moderation.performance-metrics') || 'Métricas de Rendimiento' }}
            </h5>
            <feather-icon
              :icon="isPerformanceOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted"
            />
          </div>

          <b-collapse v-model="isPerformanceOpen" class="mt-3">
            <div class="row text-center">
              <div class="col-6 mb-3">
                <div class="metric-item">
                  <h4 class="text-primary mb-1">{{ averageResponseTime }}m</h4>
                  <small class="text-muted">
                    {{ $t('backoffice.moderation.avg-response-time') || 'Tiempo Promedio de Respuesta' }}
                  </small>
                </div>
              </div>
              <div class="col-6 mb-3">
                <div class="metric-item">
                  <h4 class="text-success mb-1">{{ totalItemsProcessed }}</h4>
                  <small class="text-muted">
                    {{ $t('backoffice.moderation.items-processed') || 'Elementos Procesados' }}
                  </small>
                </div>
              </div>
            </div>

            <div class="mt-3">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="fetchModeratorPerformance"
                :disabled="isLoading"
                block
              >
                <feather-icon icon="BarChartIcon" size="16" class="mr-1" />
                {{ $t('backoffice.moderation.refresh-metrics') || 'Actualizar Métricas' }}
              </b-button>
            </div>
          </b-collapse>
        </b-card>

        <b-card class="mb-1 moderation-sidebar-section">
          <div
            class="sidebar-section-header d-flex justify-content-between align-items-center"
            @click="isContentStatsOpen = !isContentStatsOpen"
          >
            <h5 class="mb-0">
              {{ $t('backoffice.moderation.content-type-stats') || 'Estadísticas por Tipo de Contenido' }}
            </h5>
            <feather-icon
              :icon="isContentStatsOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="18"
              class="text-muted"
            />
          </div>

          <b-collapse v-model="isContentStatsOpen" class="mt-3">
            <div v-if="contentTypeStats.length > 0">
              <div
                v-for="stat in contentTypeStats"
                :key="stat.type"
                class="content-type-stat mb-3 p-3 border rounded"
                @click.stop="filterByContentType(stat.type)"
                style="cursor: pointer"
              >
                <div class="d-flex flex-column align-items-center text-center">
                  <b-badge 
                    :variant="getContentTypeVariant(stat.type)" 
                    class="mb-3"
                    style="font-size: 1rem; font-weight: 600; padding: 0.5rem 1rem;"
                  >
                    {{ getContentTypeText(stat.type) }}
                  </b-badge>
                  <h2 class="mb-3 font-weight-bold">{{ stat.count }}</h2>
                  <small class="text-muted">
                    {{ stat.pending }} {{ $t('backoffice.moderation.pending') || 'Pendiente' }}
                  </small>
                </div>
              </div>
            </div>
            
            <div v-else class="text-center py-3">
              <feather-icon icon="BarChartIcon" size="32" class="text-muted mb-2" />
              <p class="text-muted mb-0">
                {{ $t('backoffice.moderation.no-content-stats') || 'No hay estadísticas de contenido disponibles' }}
              </p>
            </div>
          </b-collapse>
        </b-card>

        <!-- Moderators Card -->
        <b-card class="moderation-sidebar-section">
          <div class="mb-3 text-center">
            <h5 class="mb-1">{{ $t('backoffice.moderation.moderators') || 'Moderadores' }}</h5>
            <b-badge variant="info">
              {{ staffMembers.length }} {{ $t('backoffice.moderation.available-moderators') || 'Disponibles' }}
            </b-badge>
          </div>
          
          <!-- Moderator Filter -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.filter-by-moderator') || 'Filtrar por Moderador' }}</label>
            <b-form-select
              v-model="filters.moderatedBy"
              :options="moderatorFilterOptions"
              @change="applyFilters"
              size="sm"
            />
          </div>

          <!-- Moderator Statistics -->
          <div class="mb-3">
            <h6 class="mb-2">{{ $t('backoffice.moderation.moderator-workload') || 'Carga de Trabajo del Moderador' }}</h6>
            <div v-if="moderatorWorkload.length > 0" class="moderator-workload-scroll">
              <div
                v-for="workload in moderatorWorkload"
                :key="workload.moderatorKey"
                class="workload-item p-1 mb-1 border rounded-xlg shadow-sm"
                :class="{ 'active-moderator': filters.moderatedBy === workload.moderatorKey }"
                @click="filterByModerator(workload.moderatorKey)"
                style="cursor: pointer;"
              >
                <div class="d-flex flex-column">
                  <div class="d-flex align-items-center justify-content-center mb-1 text-center">
                    <b-avatar
                      size="30"
                      :src="getImageSrc(workload.moderator.avatarURL)"
                      class="mr-2"
                    />
                    <div class="font-weight-bold small">
                      {{ workload.moderator.name }} <template v-if="workload.moderator.surname"> {{ workload.moderator.surname }}</template>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-center mt-0"
                    style="justify-content: center; gap: 8px; flex-wrap: nowrap; width: 100%;"
                  >
                    <b-badge
                      pill
                      variant="warning"
                      class="d-inline-flex align-items-center text-nowrap"
                      style="font-size: 10px; line-height: 1; padding: 2px 8px; border-radius: 999px; flex: 0 0 auto;"
                    >
                      {{ workload.pending }} {{ $t('backoffice.moderation.pending') || 'Pendiente' }}
                    </b-badge>
                    <b-badge
                      pill
                      variant="success"
                      class="d-inline-flex align-items-center text-nowrap"
                      style="font-size: 10px; line-height: 1; padding: 2px 8px; border-radius: 999px; flex: 0 0 auto;"
                    >
                      {{ workload.completed }} {{ $t('backoffice.moderation.completed') || 'Completado' }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          
        </b-card>
          </div>
        </div>
      </div>

      <!-- Right Column: Content -->
      <div class="col-lg-8 col-md-8">
        <!-- Content Panel -->
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="d-flex align-items-center">
              <h5 class="mb-0 mr-3">{{ $t('backoffice.moderation.content-to-moderate') || 'Contenido a Moderar' }}</h5>
              <b-badge variant="warning" class="mr-2">
                {{ filteredContent.length }} {{ $t('backoffice.moderation.pending-items') || 'Elementos Pendientes' }}
              </b-badge>
            </div>
            <div class="d-flex align-items-center">
              <!-- Bulk Actions -->
              <div v-if="selectedItems.length > 0" class="mr-3">
                <b-dropdown variant="primary" text="Acciones Masivas" class="mr-2">
                  <b-dropdown-item @click="bulkApprove">
                    <feather-icon icon="CheckIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-approve') || 'Aprobar Seleccionados' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                  <b-dropdown-item @click="bulkReject">
                    <feather-icon icon="XIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-reject') || 'Rechazar Seleccionados' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item @click="showBulkAssignModal">
                    <feather-icon icon="UserIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-assign') || 'Asignar Moderador' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                </b-dropdown>
                <b-button variant="outline-secondary" size="sm" @click="clearSelection">
                  {{ $t('backoffice.moderation.clear-selection') || 'Limpiar Selección' }}
                </b-button>
              </div>
              
              <!-- Select All -->
              <b-button
                v-if="filteredContent.length > 0"
                variant="outline-primary"
                size="sm"
                @click="toggleSelectAll"
                class="mr-2"
              >
                {{ isAllSelected ? ($t('backoffice.moderation.deselect-all') || 'Deseleccionar Todo') : ($t('backoffice.moderation.select-all') || 'Seleccionar Todo') }}
              </b-button>
            </div>
          </div>

          <div v-if="filteredContent.length === 0" class="text-center py-5">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-3" />
            <h5>{{ $t('backoffice.moderation.no-pending-content') || 'No hay Contenido Pendiente' }}</h5>
            <p class="text-muted">{{ $t('backoffice.moderation.all-content-moderated') || 'Todo el contenido ha sido moderado' }}</p>
          </div>

          <div v-else>
            <!-- Pagination Controls (Top) -->
            <div v-if="pagination.total > pagination.perPage" class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center">
                <span class="text-muted mr-3">
                  {{ $t('backoffice.moderation.showing-results', { 
                    from: ((pagination.currentPage - 1) * pagination.perPage) + 1,
                    to: Math.min(pagination.currentPage * pagination.perPage, pagination.total),
                    total: pagination.total 
                  }) || `Showing ${((pagination.currentPage - 1) * pagination.perPage) + 1}-${Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of ${pagination.total} results` }}
                </span>
                <b-form-select
                  v-model="pagination.perPage"
                  :options="[
                    { value: 10, text: '10 por página' },
                    { value: 20, text: '20 por página' },
                    { value: 50, text: '50 por página' },
                    { value: 100, text: '100 por página' }
                  ]"
                  size="sm"
                  @change="onPageSizeChange"
                  style="width: 120px"
                />
              </div>
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.total"
                :per-page="pagination.perPage"
                @change="onPageChange"
                size="sm"
              />
            </div>

            <!-- Content Items -->
            <div
              v-for="item in filteredContent"
              :key="item.id"
              class="moderation-item mb-2"
              :class="{ 'selected-item': selectedItems.includes(item.id) }"
            >
              <!-- Modern header -->
              <div class="moderation-item-header">
                <div class="d-flex align-items-center">
                  <!-- Selection Checkbox -->
                  <b-form-checkbox
                    :value="item.id"
                    v-model="selectedItems"
                    class="mr-1 moderation-item-checkbox"
                    @change="onItemSelectionChange"
                  />
                  
                  <b-avatar
                    size="40"
                    :src="getImageSrc(item.user && item.user.avatarURL)"
                    class="mr-2 moderation-item-avatar"
                  />
                  <div class="moderation-item-user">
                    <div class="moderation-item-user-name">
                      {{ item.user && item.user.name }} {{ item.user && item.user.surname }}
                    </div>
                    <div class="moderation-item-date">
                      <feather-icon icon="CalendarIcon" size="14" class="mr-1 text-muted" />
                      <span>{{ timestampToNow(item.createdAt) }}</span>
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge 
                    :variant="getStatusVariant(item.moderationStatus)" 
                    class="moderation-status-pill mr-2"
                    pill
                  >
                    {{ getStatusText(item.moderationStatus) }}
                  </b-badge>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="moderation-view-button"
                    @click="viewContent(item)"
                  >
                    {{ $t('backoffice.moderation.view-content') || 'Ver Contenido' }}
                  </b-button>
                </div>
              </div>

              <!-- Content meta tags -->
              <div class="moderation-item-tags" v-if="item.contentType || getCommentSourceInfo(item)">
                <span v-if="item.contentType" class="moderation-tag-chip">
                  {{ getContentTypeText(item.contentType) }}
                </span>
                <span
                  v-if="getCommentSourceInfo(item)"
                  class="moderation-tag-chip moderation-tag-secondary"
                >
                  {{ getCommentSourceInfo(item) }}
                </span>
              </div>

              <hr class="moderation-item-divider" />

              <!-- Moderator Assignment Row -->
              <div class="moderation-moderator-row mb-1">
                <div class="d-flex align-items-center">
                  <feather-icon icon="UserIcon" size="18" class="mr-2 text-muted" />
                  <span class="moderation-moderator-label mr-2">
                    {{ $t('backoffice.moderation.moderated-by') || 'Moderador' }}:
                  </span>
                  <template v-if="item.assignedModerator">
                    <b-avatar
                      size="28"
                      :src="getImageSrc(item.assignedModerator.avatarURL)"
                      class="mr-2"
                    />
                    <span class="moderation-moderator-name">
                      {{ item.assignedModerator.name }} {{ item.assignedModerator.surname }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="moderation-moderator-unassigned d-flex align-items-center">
                      <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
                      {{ $t('backoffice.moderation.no-moderator-assigned') || 'Sin moderador asignado' }}
                    </span>
                  </template>
                </div>
                <div class="moderator-selector">
                  <b-form-select
                    :value="item.assignedModerator ? item.assignedModerator.key : ''"
                    :options="moderatorOptions"
                    @change="updateModeratorAssignment(item, $event)"
                    size="sm"
                  />
                </div>
              </div>

              <!-- Item Content -->
              <div class="mb-3">
                <template v-if="item.type === 'answer'">
                  <div class="moderation-item-context ">
                    {{ $t('backoffice.moderation.answer-to') || 'Respuesta a' }} ·
                    {{ $t('backoffice.moderation.forum') || 'Foro' }}:
                    {{ getForumName(item.forumKey) }}
                  </div>
                  <div v-html="translate(item.description)" class="content-preview" />
                </template>
                <template v-else>
                  <div class="moderation-item-context mb-1">
                    {{ $t('backoffice.moderation.comment-on') || 'Comentario en' }} ·
                    <span class="text-muted">{{ getCommentSourceInfo(item) }}</span>
                  </div>
                  <div v-if="item.content" v-html="item.content" class="content-preview" />
                  <div v-else-if="!getModeratorFeedback(item)" class="text-muted">
                    <em>{{ $t('backoffice.moderation.no-content-available')}}</em>
                  </div>
                </template>
              </div>

              <!-- Moderator comment block -->
              <div v-if="getModeratorFeedback(item)" class="moderation-comment-card ">
                <div class="d-flex">
                  <div class="moderation-comment-icon mr-2">
                    <feather-icon icon="MessageCircleIcon" size="18" />
                  </div>
                  <div class="flex-grow-1">
                    <div class="moderation-comment-label">
                      {{ $t('backoffice.moderation.comment-on') || 'Comentario del moderador' }}
                    </div>
                    <div class="moderation-comment-text">
                      “{{ getModeratorFeedback(item) }}”
                    </div>
                  </div>
                </div>
              </div>

              <!-- Moderation History -->
              <div v-if="item.moderationHistory && item.moderationHistory.length > 0" class="mb-3">
                <h6 class="mb-2">
                  <feather-icon icon="ClockIcon" size="16" class="mr-1" />
                  {{ $t('backoffice.moderation.moderation-history') || 'Moderation History' }}
                </h6>
                <div class="moderation-history">
                  <div
                    v-for="(history, index) in item.moderationHistory"
                    :key="index"
                    class="history-item p-2 mb-2 border-left border-3"
                    :class="getHistoryItemClass(history.action)"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{{ getHistoryActionText(history.action) }}</strong>
                        <span class="text-muted ml-2">
                          {{ timestampToNow(history.timestamp) }}
                        </span>
                      </div>
                      <b-badge :variant="getHistoryBadgeVariant(history.action)">
                        {{ getHistoryActionText(history.action) }}
                      </b-badge>
                    </div>
                    <div v-if="history.moderator" class="mt-1">
                      <small class="text-muted">
                        {{ $t('backoffice.moderation.moderated-by') || 'Moderated by' }}: 
                        {{ history.moderator.name }} {{ history.moderator.surname }}
                      </small>
                    </div>
                    <div v-if="history.feedback" class="mt-1">
                      <small class="text-muted">
                        {{ $t('backoffice.moderation.feedback') || 'Feedback' }}: {{ history.feedback }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Use existing ModerationSection component -->
              <moderation-section
                :content="item"
                :morph-type="item.type === 'answer' ? 'forums' : (item.morphType || 'comment')"
                :item-type="item.type === 'answer' ? 'forumAnswers' : 'comments'"
                :item-type-name="item.type === 'answer' ? 'answer' : 'comment'"
                :parent-key="item.type === 'answer' ? item.forumKey : getCommentParentKey(item)"
                :moderator-options="moderatorOptions"
                :on-assign-moderator="assignModerator"
                :can-moderate=true
                @moderation-complete="handleModerationComplete"
              />
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Bulk Assign Moderator Modal -->
    <b-modal
      id="bulk-assign-moderator-modal"
      :title="$t('backoffice.moderation.bulk-assign-moderator') || 'Assign Moderator to Selected Items'"
      centered
    >
      <div class="mb-3">
        <p>{{ $t('backoffice.moderation.bulk-assign-description', { count: selectedItems.length }) || `Assign a moderator to ${selectedItems.length} selected items` }}</p>
      </div>
      
      <b-form-group :label="$t('backoffice.moderation.select-moderator') || 'Select Moderator'">
        <b-form-select
          v-model="bulkAssignModerator"
          :options="moderatorOptions"
          :state="bulkAssignModerator ? null : false"
        />
      </b-form-group>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('bulk-assign-moderator-modal')"
        >
          {{ $t('action.cancel') || 'Cancel' }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!bulkAssignModerator"
          @click="confirmBulkAssignModerator(bulkAssignModerator)"
        >
          {{ $t('backoffice.moderation.assign') || 'Assign Moderator' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { getImageResource } from '@/@core/utils/image-utils';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';

export default {
  name: 'ModerationDashboard',
  
  components: {
    ModerationSection
  },

  mixins: [ToastNotificationsMixin],

  data() {
    return {
      isLoading: true,
      isPerformanceOpen: false,
      isContentStatsOpen: false,
      searchQuery: '',
      searchTimeout: null,
      selectedItems: [],
      moderationQueue: [],
      moderationStats: {
        pending: 0,
        approved: 0,
        rejected: 0
      },
      activeModerators: 0,
      filters: {
        contentType: 'all',
        moderationStatus: 'pending',
        forumKey: '',
        moderatedBy: '',
        dateFrom: null,
        dateTo: null
      },
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0
      },
      forums: [],
      staffMembers: [],
      bulkAssignModerator: '',
      moderatorPerformance: [],
      averageResponseTime: 0,
      totalItemsProcessed: 0,
      updateInterval: null,
      moderatorWorkload: [],
      contentTypeStats: []
    };
  },

  computed: {
    collective() {
      return this.$store.state.collective.collective;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    isAllSelected() {
      return this.filteredContent.length > 0 && this.selectedItems.length === this.filteredContent.length;
    },
    contentTypeOptions() {
      return [
        { value: 'all', text: this.$t('backoffice.moderation.all-content') || 'Todo el Contenido' },
        { value: 'questions', text: this.$t('backoffice.moderation.questions') || 'Preguntas del Foro' },
        { value: 'answers', text: this.$t('backoffice.moderation.answers') || 'Respuestas del Foro' },
        { value: 'posts', text: this.$t('backoffice.moderation.posts') || 'Publicaciones' },
        { value: 'comments', text: this.$t('backoffice.moderation.comments') || 'Comentarios' }
        // TODO: Uncomment when frontend implementation is ready
        // { value: 'articles', text: this.$t('backoffice.moderation.articles') || 'Articles' },
        // { value: 'blogs', text: this.$t('backoffice.moderation.blogs') || 'Blogs' },
        // { value: 'challenges', text: this.$t('backoffice.moderation.challenges') || 'Challenges' },
        // { value: 'chats', text: this.$t('backoffice.moderation.chats') || 'Chats' },
        // { value: 'products', text: this.$t('backoffice.moderation.products') || 'Products' },
        // { value: 'links', text: this.$t('backoffice.moderation.links') || 'Links' },
        // { value: 'organizations', text: this.$t('backoffice.moderation.organizations') || 'Organizations' },
        // { value: 'communities', text: this.$t('backoffice.moderation.communities') || 'Communities' },
        // { value: 'events', text: this.$t('backoffice.moderation.events') || 'Events' },
        // { value: 'ideas', text: this.$t('backoffice.moderation.ideas') || 'Ideas' },
        // { value: 'jobs', text: this.$t('backoffice.moderation.jobs') || 'Jobs' },
        // { value: 'work_items', text: this.$t('backoffice.moderation.work-items') || 'Work Items' },
        // { value: 'tenders', text: this.$t('backoffice.moderation.tenders') || 'Tenders' },
        // { value: 'media', text: this.$t('backoffice.moderation.media') || 'Media' },
        // { value: 'entities', text: this.$t('backoffice.moderation.entities') || 'Entities' },
        // { value: 'people', text: this.$t('backoffice.moderation.people') || 'People' },
        // { value: 'polls', text: this.$t('backoffice.moderation.polls') || 'Polls' }
      ];
    },
    statusOptions() {
      return [
        { value: 'pending', text: this.$t('backoffice.moderation.pending') || 'Pendiente' },
        { value: 'approved', text: this.$t('backoffice.moderation.approved') || 'Aprobado' },
        { value: 'rejected', text: this.$t('backoffice.moderation.rejected') || 'Rechazado' },
        { value: 'all', text: this.$t('backoffice.moderation.all-statuses') || 'Todos los Estados' }
      ];
    },
    forumOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.all-forums') || 'Todos los Foros' }
      ];
      
      this.forums.filter(this.hasValidForumName).forEach(forum => {
        options.push({
          value: forum.key,
          text: this.translate(forum.name)
        });
      });
      
      return options;
    },
    filteredContent() {
      // Use the moderationQueue directly since it contains the API response data
      let content = [...this.moderationQueue];
      
      // Apply content type filter
      if (this.filters.contentType !== 'all') {
        content = content.filter(item => item.contentType === this.filters.contentType);
      }
      
      // Apply status filter
      if (this.filters.moderationStatus !== 'all') {
        const targetStatus = this.filters.moderationStatus;
        content = content.filter(item => this.normalizeStatus(item.moderationStatus) === targetStatus);
      }
      
      // Apply forum filter
      if (this.filters.forumKey !== '') {
        const forumKey = this.filters.forumKey;
        const forumRecord = this.forums.find(f => f.key === forumKey);
        const forumId = forumRecord && (forumRecord.id ?? forumRecord.forumID);
        content = content.filter(item => {
          if (item.forumKey) return item.forumKey === forumKey;
          if (item.mainSpaceID != null && forumId != null) return String(item.mainSpaceID) === String(forumId);
          return String(item.mainSpaceID) === String(forumKey);
        });
      }
      
      // Apply moderator filter
      if (this.filters.moderatedBy !== '') {
        if (this.filters.moderatedBy === 'unassigned') {
          content = content.filter(item => !item.assignedModerator && !item.assignedModeratorKey);
        } else {
          const by = this.filters.moderatedBy;
          const numeric = Number(by);
          const targetId = Number.isNaN(numeric) ? null : numeric;
          content = content.filter(item =>
            item.assignedModerator?.key === by ||
            item.assignedModeratorKey === by ||
            (targetId !== null && (item.assignedModerator?.id === targetId))
          );
        }
      }
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        content = content.filter(item => 
          (item.contentKey && item.contentKey.toLowerCase().includes(query)) ||
          (item.user && item.user.name && item.user.name.toLowerCase().includes(query)) ||
          (item.user && item.user.surname && item.user.surname.toLowerCase().includes(query)) ||
          (item.assignedModerator && item.assignedModerator.name && item.assignedModerator.name.toLowerCase().includes(query)) ||
          (item.contentType && String(item.contentType).toLowerCase().includes(query))
        );
      }
      
      // Sort by creation date (newest first)
      return content.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    filteredPendingAnswers() {
      let answers = this.pendingAnswers;
      
      // Filter to show only items with moderationStatus == 0 (pending moderation)
      answers = answers.filter(answer => answer.moderationStatus === 0);
      
      if (this.selectedForum !== 'all') {
        answers = answers.filter(answer => answer.forumKey === this.selectedForum);
      }
      
      if (this.selectedModerator !== '') {
        answers = answers.filter(answer => answer.assignedModeratorKey === this.selectedModerator);
      }
      
      return answers;
    },
    filteredPendingComments() {
      let comments = this.pendingComments;
      
      // Filter to show only items with moderationStatus == 0 (pending moderation)
      comments = comments.filter(comment => comment.moderationStatus === 0);
      
      if (this.selectedForum !== 'all') {
        comments = comments.filter(comment => {
          // Now we have forumKey directly on the comment
          return comment.forumKey === this.selectedForum;
        });
      }
      
      if (this.selectedModerator !== '') {
        comments = comments.filter(comment => comment.assignedModeratorKey === this.selectedModerator);
      }
      
      return comments;
    },
    moderatorOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.select-moderator') || 'Seleccionar Moderador' }
      ];
      
      this.staffMembers.filter(this.hasValidModeratorName).forEach(member => {
        options.push({
          value: member.key,
          text: `${member.name} ${member.surname}`,
          member: member
        });
      });
      
      return options;
    },
    moderatorFilterOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.all-moderators') || 'Todos los Moderadores' },
        { value: 'unassigned', text: this.$t('backoffice.moderation.unassigned') || 'Sin Asignar' }
      ];
      
      this.staffMembers.filter(this.hasValidModeratorName).forEach(member => {
        options.push({
          value: member.key,
          text: `${member.name} ${member.surname}`,
          member: member
        });
      });
      
      return options;
    }
  },

  async created() {
    if (!this.isStaff) {
      this.$router.push({ name: 'backoffice' });
      return;
    }
    await this.fetchData();
    this.updateBreadcrumbs();
    this.setupRealTimeUpdates();
  },

  beforeDestroy() {
    this.stopRealTimeUpdates();
  },

  methods: {
    getImageSrc(url) {
      return getImageResource(url);
    },
    translate(field) {
      return translateTranslationTable(this.currentLocale, field);
    },
    timestampToNow(timestamp) {
      moment.locale(this.currentLocale);
      return moment(timestamp).format('DD/MM/YYYY HH:mm');
    },
    hasValidModeratorName(member) {
      const name = String(member?.name || '').trim();
      return name.length > 0 && name !== '-';
    },
    hasValidForumName(forum) {
      const translated = String(this.translate(forum?.name) || '').trim();
      return translated.length > 0 && translated !== '-';
    },
    normalizeStatus(status) {
      if (typeof status !== 'number') return status;
      const map = { 0: 'pending', 1: 'approved', 2: 'rejected' };
      return map[status] != null ? map[status] : String(status);
    },
    getForumName(forumKey) {
      const forum = this.forums.find(f => f.key === forumKey);
      return forum ? this.translate(forum.name) : (this.$t('backoffice.moderation.unknown-forum') || 'Foro Desconocido');
    },
    getForumAnswerName(forumAnswerKey) {
      const forumAnswer = this.pendingAnswers.find(f => f.key === forumAnswerKey);
      return forumAnswer ? this.translate(forumAnswer.name) : (this.$t('backoffice.moderation.unknown-forum-answer') || 'Respuesta de Foro Desconocida');
    },
    getCommentSourceInfo(comment) {
      // Try to get source information from different possible fields
      if (comment.morphType === 'answer' && comment.forumKey) {
        return `${this.$t('backoffice.moderation.comment-to') || 'Comentario a'} "${this.getForumAnswerName(comment.forumKey)}"`;
      }
      
      // Check for other source information
      if (comment.sourceTitle) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: "${comment.sourceTitle}"`;
      }
      
      if (comment.parentTitle) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: "${comment.parentTitle}"`;
      }
      
      if (comment.sourceKey) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: ${comment.sourceKey}`;
      }
      
      // If we have content, show a preview instead of "Unknown source"
      if (comment.content && comment.content.trim()) {
        const contentPreview = comment.content.length > 100 
          ? comment.content.substring(0, 100) + '...' 
          : comment.content;
        return `${this.$t('backoffice.moderation.comment-content') || 'Contenido del comentario'}: "${contentPreview}"`;
      }
      
      return '';
    },
    getCommentParentKey(comment) {
      // For comments on answers, use the answer key as parent
      if (comment.morphType === 'answer') {
        return comment.key;
      }
      // For other comment types, use the source key
      return comment.sourceKey || comment.key;
    },
    async fetchStaffMembers() {
      try {
        // Fetch staff members (response structure may vary)
        const response = await this.$store.dispatch('getItems', {
          itemType: 'backofficeStaff',
          storedKey: 'backofficeStaff',
          customName: 'communityMembers',
          forceAPICall: true,
          page: 1,
          perPage: 100,
          requestConfig: {
            isStaff: 1,
            isForBackoffice: 1,
          }
        });

        // Normalize response to an array
        let rawMembers = [];
        if (response && Array.isArray(response.data)) {
          rawMembers = response.data;
        } else if (Array.isArray(response)) {
          rawMembers = response;
        } else if (response && Array.isArray(response.items)) {
          rawMembers = response.items;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          rawMembers = response.data.data;
        } else {
          console.warn('Unexpected staff members response structure:', response);
        }

        // Filter to get only staff members (fallback to role check if needed)
        this.staffMembers = (rawMembers || []).filter(member => {
          if (!member) return false;
          if (member.isStaff) return true;
          const roles = member.roles || [];
          return roles.some(role => role && (role.name === 'Owner' || role.name === 'Moderator' || role.name === 'Admin'));
        });

        if (Array.isArray(this.moderationQueue) && this.moderationQueue.length >= 0) {
          await this.calculateModeratorWorkload();
        }
      } catch (error) {
        console.error('Error fetching staff members:', error);
        this.staffMembers = [];
      }
    },
    async fetchData() {
      this.isLoading = true;

      try {
        // Fetch data in parallel for better performance
        const promises = [
          this.fetchModerationQueue(),
          this.fetchStaffMembers(),
          this.fetchForums()
        ];
        
        // Wait for all promises to resolve, but don't fail if some fail
        await Promise.allSettled(promises);
        
        // Calculate stats from the moderation queue data
        this.calculateModerationStats();
        await this.calculateModeratorWorkload();
        
        // Show a general success message if at least some data was fetched
        if (this.moderationQueue.length > 0 || this.staffMembers.length > 0) {
          console.log('Moderation data loaded successfully');
        }
        
      } catch (error) {
        console.error('Error fetching moderation data:', error);
        this.notifyError(this.$t('error-message.general-error') || 'An error occurred while loading moderation data');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchModerationQueue() {
      try {
        const cleanFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => 
            value !== '' && value !== null && value !== undefined
          )
        );
        
        // Rename moderatedBy to moderatorKey for API compatibility
        const apiFilters = { ...cleanFilters };
        if (apiFilters.moderatedBy) {
          apiFilters.moderatorKey = apiFilters.moderatedBy;
          delete apiFilters.moderatedBy;
        }
        
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: this.pagination.currentPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...apiFilters,
            count: this.pagination.perPage,
            offset: (this.pagination.currentPage - 1) * this.pagination.perPage
          }
        });
        this.moderationQueue = response?.data || [];
        this.pagination.total = response?.total || 0;
        
        // Calculate stats from the fetched data
        this.calculateModerationStats();
        
        // Calculate moderator workload after fetching data
        await this.calculateModeratorWorkload();
        
        // Calculate content type statistics
        await this.calculateContentTypeStats();
        
        console.log('Fetched moderation queue:', this.moderationQueue.length);
      } catch (error) {
        console.error('Error fetching moderation queue:', error);
        this.moderationQueue = [];
        this.pagination.total = 0;
        
        // Handle specific error types
        if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
          this.notifyError(this.$t('backoffice.moderation.network-error') || 'Network error. Please check your connection and try again.');
        } else if (error.response?.status === 503) {
          this.notifyError(this.$t('backoffice.moderation.service-unavailable') || 'Moderation service is temporarily unavailable. Please try again later.');
        } else if (error.response?.status === 403) {
          this.notifyError(this.$t('backoffice.moderation.access-denied') || 'Access denied. You may not have permission to access moderation data.');
        } else {
          this.notifyError(this.$t('backoffice.moderation.fetch-error') || 'Error fetching moderation data. Please try again.');
        }
      }
    },

    calculateModerationStats() {
      try {
        const source = (this.filteredContent && Array.isArray(this.filteredContent) && this.filteredContent.length > 0)
          ? this.filteredContent
          : (this.moderationQueue || []);

        if (source && Array.isArray(source)) {
          this.moderationStats = {
            pending: source.filter(item => this.normalizeStatus(item.moderationStatus) === 'pending').length,
            approved: source.filter(item => this.normalizeStatus(item.moderationStatus) === 'approved').length,
            rejected: source.filter(item => this.normalizeStatus(item.moderationStatus) === 'rejected').length
          };
        } else {
          this.moderationStats = {
            pending: 0,
            approved: 0,
            rejected: 0
          };
        }
        
        console.log('Calculated moderation stats:', this.moderationStats);
      } catch (error) {
        console.error('Error calculating moderation stats:', error);
        this.moderationStats = {
          pending: 0,
          approved: 0,
          rejected: 0
        };
      }
    },

    async fetchForums() {
      try {
        await this.$store.dispatch('getItems', {
          itemType: 'forums',
          storedKey: 'list',
          forceAPICall: true,
          page: 1,
          perPage: 100
        });
        
        const forumsData = this.$store.getters.forums;
        this.forums = forumsData.list?.unpaginated || [];
      } catch (error) {
        console.error('Error fetching forums:', error);
        this.forums = [];
      }
    },
    async refreshData() {
      await this.fetchData();
      this.notifySuccess(this.$t('backoffice.moderation.data-refreshed') || 'Moderation data refreshed successfully');
    },
    async handleModerationComplete({ action, contentKey, response }) {
      // Show success notification
      this.notifySuccess(
        this.$t(`backoffice.moderation.success-${action}`) || `Action ${action} completed successfully`
      );

      // Refresh data to update the list
      await this.fetchData();
    },
    viewForum(forumKey) {
      this.$router.push({ name: 'forum', params: { id: forumKey } });
    },
    viewCommentSource(comment) {
      // Navigate to the source of the comment
      if (comment.morphType === 'answer' && comment.forumKey) {
        this.viewForum(comment.forumKey);
      }
    },
    updateBreadcrumbs() {
      const breadcrumbs = [
        {
          text: this.$t('backoffice.title') || 'Backoffice',
          to: { name: 'backoffice' }
        },
        {
          text: this.$t('backoffice.moderation.dashboard-title') || 'Moderation Dashboard',
          active: true
        }
      ];
      this.$store.commit('app/SET_BREADCRUMBS', breadcrumbs);
    },
    async assignModerator(content, moderatorKey) {
      try {
        // Update the content with assigned moderator
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: content.morphType === 'answer' ? 'forumAnswers' : 'comments',
            morphType: content.morphType || 'forums',
            requestConfig: {
              [`${content.morphType === 'answer' ? 'forumAnswer' : 'comment'}Key`]: content.key,
              parentKey: content.morphType === 'answer' ? content.forumKey : content.answerKey,
              assignedModeratorKey: moderatorKey
            },
          },
        });

        // Update local data
        if (content.morphType === 'answer') {
          const answerIndex = this.pendingAnswers.findIndex(a => a.key === content.key);
          if (answerIndex !== -1) {
            this.pendingAnswers[answerIndex].assignedModeratorKey = moderatorKey;
          }
        } else {
          const commentIndex = this.pendingComments.findIndex(c => c.key === content.key);
          if (commentIndex !== -1) {
            this.pendingComments[commentIndex].assignedModeratorKey = moderatorKey;
          }
        }

        this.notifySuccess(this.$t('backoffice.moderation.moderator-assigned') || 'Moderator assigned successfully');
      } catch (error) {
        console.error('Error assigning moderator:', error);
        this.notifyError(this.$t('backoffice.moderation.moderator-assign-error') || 'Error assigning moderator');
      }
    },
    getModeratorName(moderatorKey) {
      const moderator = this.staffMembers.find(m => m.key === moderatorKey);
      return moderator ? `${moderator.name} ${moderator.surname}` : '';
    },
    getSelectedModeratorData() {
      return this.staffMembers.find(m => m.key === this.selectedModerator);
    },
    getRoleVariant(roleName) {
      switch (roleName) {
        case 'Owner':
          return 'warning';
        case 'Admin':
          return 'danger';
        case 'Moderator':
          return 'info';
        default:
          return 'secondary';
      }
    },
    // New methods for enhanced functionality
    async applyFilters() {
      this.pagination.currentPage = 1;
      await this.fetchModerationQueue();
    },

    onSearchInput() {
      // Debounce search input
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },

    clearSearch() {
      this.searchQuery = '';
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        contentType: 'all',
        moderationStatus: 'pending',
        forumKey: '',
        moderatedBy: '',
        dateFrom: null,
        dateTo: null
      };
      this.searchQuery = '';
      this.applyFilters();
    },

    // Bulk moderation methods
    async bulkApprove() {
      if (this.selectedItems.length === 0) return;
      
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderationStatus: 'approved'
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-approve-success', { count: this.selectedItems.length }) || 
          `Successfully approved ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
      } catch (error) {
        console.error('Error in bulk approve:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-approve-error') || 'Error approving items');
      }
    },

    async bulkReject() {
      if (this.selectedItems.length === 0) return;
      
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderationStatus: 'rejected'
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-reject-success', { count: this.selectedItems.length }) || 
          `Successfully rejected ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
      } catch (error) {
        console.error('Error in bulk reject:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-reject-error') || 'Error rejecting items');
      }
    },

    async showBulkAssignModal() {
      if (this.selectedItems.length === 0) return;
      
      // Show modal to select moderator
      this.$bvModal.show('bulk-assign-moderator-modal');
    },

    async confirmBulkAssignModerator(moderatorKey) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderatedBy: moderatorKey
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-assign-success', { count: this.selectedItems.length }) || 
          `Successfully assigned moderator to ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
        this.$bvModal.hide('bulk-assign-moderator-modal');
      } catch (error) {
        console.error('Error in bulk assign moderator:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-assign-error') || 'Error assigning moderator');
      }
    },

    // Selection methods
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.clearSelection();
      } else {
        this.selectedItems = this.filteredContent.map(item => item.id);
      }
    },

    clearSelection() {
      this.selectedItems = [];
    },

    onItemSelectionChange() {
      // Handle individual item selection changes
    },

    // Utility methods
    getStatusVariant(status) {
      switch (status) {
        case 'pending': return 'warning';
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        default: return 'secondary';
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'pending': return this.$t('backoffice.moderation.pending') || 'Pendiente';
        case 'approved': return this.$t('backoffice.moderation.approved') || 'Aprobado';
        case 'rejected': return this.$t('backoffice.moderation.rejected') || 'Rechazado';
        default: return status;
      }
    },

    getContentTypeVariant(contentType) {
      const variants = {
        'questions': 'primary',
        'answers': 'info',
        'posts': 'success',
        'comments': 'secondary',
        'articles': 'warning',
        'blogs': 'dark',
        'challenges': 'danger',
        'chats': 'light',
        'products': 'primary',
        'links': 'info',
        'organizations': 'success',
        'communities': 'warning',
        'events': 'dark',
        'ideas': 'danger',
        'jobs': 'light',
        'work_items': 'primary',
        'tenders': 'info',
        'media': 'success',
        'entities': 'warning',
        'people': 'dark',
        'polls': 'danger'
      };
      return variants[contentType] || 'secondary';
    },

    getContentTypeText(contentType) {
      const texts = {
        'questions': this.$t('backoffice.moderation.questions') || 'Preguntas',
        'answers': this.$t('backoffice.moderation.answers') || 'Respuestas',
        'posts': this.$t('backoffice.moderation.posts') || 'Publicaciones',
        'comments': this.$t('backoffice.moderation.comments') || 'Comentarios',
        'articles': this.$t('backoffice.moderation.articles') || 'Artículos',
        'blogs': this.$t('backoffice.moderation.blogs') || 'Blogs',
        'challenges': this.$t('backoffice.moderation.challenges') || 'Desafíos',
        'chats': this.$t('backoffice.moderation.chats') || 'Chats',
        'products': this.$t('backoffice.moderation.products') || 'Productos',
        'links': this.$t('backoffice.moderation.links') || 'Enlaces',
        'organizations': this.$t('backoffice.moderation.organizations') || 'Organizaciones',
        'communities': this.$t('backoffice.moderation.communities') || 'Comunidades',
        'events': this.$t('backoffice.moderation.events') || 'Eventos',
        'ideas': this.$t('backoffice.moderation.ideas') || 'Ideas',
        'jobs': this.$t('backoffice.moderation.jobs') || 'Empleos',
        'work_items': this.$t('backoffice.moderation.work-items') || 'Elementos de Trabajo',
        'tenders': this.$t('backoffice.moderation.tenders') || 'Licitaciones',
        'media': this.$t('backoffice.moderation.media') || 'Medios',
        'entities': this.$t('backoffice.moderation.entities') || 'Entidades',
        'people': this.$t('backoffice.moderation.people') || 'Personas',
        'polls': this.$t('backoffice.moderation.polls') || 'Encuestas'
      };
      return texts[contentType] || contentType;
    },

    viewContent(item) {
      console.log('viewContent - item:', item);
      try {
        // Forums: questions (forum thread) and answers (go to parent forum)
        if (item.contentType === 'questions') {
          return this.openInNewTab('forum', { id: item.contentKey });
        }
        if (item.contentType === 'answers') {
          const forumKey = item.forumKey || item.sourceKey || item.contentKey;
          if (forumKey) return this.openInNewTab('forum', { id: forumKey });
        }

        // Posts: go to post details
        if (item.contentType === 'posts') {
          const postKey = item.contentKey || item.sourceKey || item.parentKey;
          if (postKey) return this.openInNewTab('post-details', { postKey });
        }

        // Comments: route depending on the target they belong to
        if (item.contentType === 'comments') {
          const morph = (item.morphType || '').toLowerCase();
          // Comment on a forum answer -> go to the forum thread
          if (morph === 'answer' && (item.forumKey || item.sourceKey)) {
            return this.openInNewTab('forum', { id: item.forumKey || item.sourceKey });
          }
          // Comment on a post -> go to the post details
          if (morph === 'post' || item.sourceType === 'post') {
            const postKey = item.sourceKey || item.parentKey || item.contentKey;
            if (postKey) return this.openInNewTab('post-details', { postKey });
          }
          // Best-effort fallback for comments with just a sourceKey (likely a post)
          if (item.sourceKey) {
            return this.openInNewTab('post-details', { postKey: item.sourceKey });
          }
        }
      } catch (error) {
        console.warn(`Navigation error for content type: ${item && item.contentType}`, error);
      }
      // As a last resort, show preview modal (if available)
      this.$bvModal && this.$bvModal.show('content-preview-modal');
    },

    // Helper method to open content in new tab
    openInNewTab(routeName, params) {
      try {
        // Generate the URL using Vue Router's resolve method
        const route = this.$router.resolve({ name: routeName, params });
        if (route && route.href) {
          window.open(route.href, '_blank');
        } else {
          console.warn(`Could not resolve route for ${routeName} with params:`, params);
          this.$bvModal.show('content-preview-modal');
        }
      } catch (error) {
        console.error(`Error opening ${routeName} in new tab:`, error);
        this.$bvModal.show('content-preview-modal');
      }
    },

    // Pagination methods
    async onPageChange(page) {
      this.pagination.currentPage = page;
      await this.fetchModerationQueue();
      this.scrollToTop();
    },

    async onPageSizeChange() {
      this.pagination.currentPage = 1;
      await this.fetchModerationQueue();
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // Lazy loading and performance optimization
    async loadMoreItems() {
      if (this.isLoading || this.pagination.currentPage * this.pagination.perPage >= this.pagination.total) {
        return;
      }

      this.isLoading = true;
      try {
        const nextPage = this.pagination.currentPage + 1;
        
        // Rename moderatedBy to moderatorKey for API compatibility
        const apiFilters = { ...this.filters };
        if (apiFilters.moderatedBy) {
          apiFilters.moderatorKey = apiFilters.moderatedBy;
          delete apiFilters.moderatedBy;
        }
        
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: nextPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...apiFilters,
            count: this.pagination.perPage,
            offset: (nextPage - 1) * this.pagination.perPage
          }
        });

        // Append new items to existing queue
        this.moderationQueue.push(...(response.data || []));
        this.pagination.currentPage = nextPage;
      } catch (error) {
        console.error('Error loading more items:', error);
        this.notifyError(this.$t('backoffice.moderation.load-more-error') || 'Error loading more items');
      } finally {
        this.isLoading = false;
      }
    },

    // Virtual scrolling for large datasets
    setupVirtualScrolling() {
      // This would be implemented with a virtual scrolling library
      // For now, we'll use pagination as the primary optimization
    },

    // Cache management
    clearCache() {
      // Clear cached moderation data
      this.$store.commit('moderation/CLEAR_CACHE');
    },

    // Performance monitoring
    trackPerformance(action, duration) {
      console.log(`Performance: ${action} took ${duration}ms`);
      // Could send to analytics service
    },

    // Audit trail methods
    getHistoryItemClass(action) {
      switch (action) {
        case 'approved': return 'border-success bg-light-success';
        case 'rejected': return 'border-danger bg-light-danger';
        case 'assigned': return 'border-info bg-light-info';
        case 'escalated': return 'border-warning bg-light-warning';
        default: return 'border-secondary bg-light';
      }
    },

    getHistoryActionText(action) {
      switch (action) {
        case 'approved': return this.$t('backoffice.moderation.approved') || 'Approved';
        case 'rejected': return this.$t('backoffice.moderation.rejected') || 'Rejected';
        case 'assigned': return this.$t('backoffice.moderation.assigned') || 'Assigned';
        case 'escalated': return this.$t('backoffice.moderation.escalated') || 'Escalated';
        default: return action;
      }
    },

    getHistoryBadgeVariant(action) {
      switch (action) {
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        case 'assigned': return 'info';
        case 'escalated': return 'warning';
        default: return 'secondary';
      }
    },

    async fetchModerationHistory(itemId) {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderationHistory',
          storedKey: `history-${itemId}`,
          forceAPICall: true,
          requestConfig: {
            moderationId: itemId
          }
        });

        return response.data || [];
      } catch (error) {
        console.error('Error fetching moderation history:', error);
        return [];
      }
    },

    // Performance metrics methods
    async fetchModeratorPerformance() {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderatorPerformance',
          storedKey: 'performance',
          forceAPICall: true,
          requestConfig: {
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo
          }
        });

        this.moderatorPerformance = response.data || [];
        this.calculatePerformanceMetrics();
      } catch (error) {
        console.error('Error fetching moderator performance:', error);
        this.moderatorPerformance = [];
      }
    },

    calculatePerformanceMetrics() {
      if (!this.moderatorPerformance.length) return;

      // Calculate average response time
      const totalResponseTime = this.moderatorPerformance.reduce((sum, mod) => 
        sum + (mod.averageResponseTime || 0), 0);
      this.averageResponseTime = totalResponseTime / this.moderatorPerformance.length;

      // Calculate total items processed
      this.totalItemsProcessed = this.moderatorPerformance.reduce((sum, mod) => 
        sum + (mod.itemsProcessed || 0), 0);

      // Calculate active moderators
      this.activeModerators = this.moderatorPerformance.filter(mod => 
        mod.itemsProcessed > 0).length;
    },

    // Workflow improvements
    async escalateItem(itemId, reason) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationEscalate',
            requestConfig: {
              moderationId: itemId,
              reason: reason,
              escalatedBy: this.$store.state.auth.user.id
            }
          }
        });

        this.notifySuccess(this.$t('backoffice.moderation.escalated-success') || 'Item escalated successfully');
        await this.fetchData();
      } catch (error) {
        console.error('Error escalating item:', error);
        this.notifyError(this.$t('backoffice.moderation.escalate-error') || 'Error escalating item');
      }
    },

    async addModerationNote(itemId, note) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationNote',
            requestConfig: {
              moderationId: itemId,
              note: note,
              addedBy: this.$store.state.auth.user.id
            }
          }
        });

        this.notifySuccess(this.$t('backoffice.moderation.note-added') || 'Note added successfully');
      } catch (error) {
        console.error('Error adding note:', error);
        this.notifyError(this.$t('backoffice.moderation.note-error') || 'Error adding note');
      }
    },

    // Real-time updates
    setupRealTimeUpdates() {
      // Disabled automatic polling to prevent database overload
      // Users can manually refresh using the refresh button
      console.log('Real-time updates disabled to prevent database overload');
    },

    stopRealTimeUpdates() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },

    // Moderator assignment methods
    async updateModeratorAssignment(item, moderatorKey) {
      try {
        const startTime = Date.now();
        
        // Call the API to update moderator assignment
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationAssignment',
            requestConfig: {
              moderationId: item.id,
              moderatorKey: moderatorKey,
              contentType: item.contentType,
              contentKey: item.contentKey
            }
          },
          noSet: true
        });

        // Update local data
        const itemIndex = this.moderationQueue.findIndex(i => i.id === item.id);
        if (itemIndex !== -1) {
          if (moderatorKey) {
            const moderator = this.staffMembers.find(m => m.key === moderatorKey);
            this.moderationQueue[itemIndex].assignedModerator = moderator;
            this.moderationQueue[itemIndex].assignedModeratorKey = moderatorKey;
          } else {
            this.moderationQueue[itemIndex].assignedModerator = null;
            this.moderationQueue[itemIndex].assignedModeratorKey = null;
          }
        }

        // Track performance
        const duration = Date.now() - startTime;
        this.trackPerformance('updateModeratorAssignment', duration);

        this.notifySuccess(
          moderatorKey 
            ? (this.$t('backoffice.moderation.moderator-assigned-success') || 'Moderator assigned successfully')
            : (this.$t('backoffice.moderation.moderator-unassigned-success') || 'Moderator unassigned successfully')
        );

        // Update moderator workload
        await this.calculateModeratorWorkload();
        
      } catch (error) {
        console.error('Error updating moderator assignment:', error);
        this.notifyError(this.$t('backoffice.moderation.moderator-assign-error') || 'Error assigning moderator');
      }
    },

    // Moderator filtering methods
    filterByModerator(moderatorKey) {
      this.filters.moderatedBy = moderatorKey;
      this.applyFilters();
    },

    async calculateModeratorWorkload() {
      try {
        const workload = [];
        
        // Use static data or queue depending on mode
        const dataSource = this.moderationQueue;
        
        // First, get unique moderators from the data
        const moderatorsInData = new Map();
        dataSource.forEach(item => {
          if (item.assignedModerator && item.assignedModerator.id) {
            moderatorsInData.set(item.assignedModerator.id, item.assignedModerator);
          }
        });
        
        // Calculate workload for each moderator in the data
        for (const [moderatorId, moderator] of moderatorsInData) {
          const pending = dataSource.filter(item => 
            item.assignedModerator?.id === moderatorId && 
            (item.moderationStatus === 'pending' || item.moderationStatus === 0)
          ).length;
          
          const completed = dataSource.filter(item => 
            item.assignedModerator?.id === moderatorId && 
            (item.moderationStatus === 'approved' || item.moderationStatus === 1 || 
             item.moderationStatus === 'rejected' || item.moderationStatus === 2)
          ).length;

          workload.push({
            moderatorKey: moderator.key || moderatorId,
            moderator: {
              key: moderator.key || moderatorId,
              name: moderator.name || moderator.username,
              surname: '', // Not available in static data
              avatarURL: moderator.avatarURL
            },
            pending: pending,
            completed: completed,
            total: pending + completed
          });
        }
        
        // Also include staff members from staffMembers if not using static data
        if (true) {
          for (const staffMember of this.staffMembers) {
            if (!workload.find(w => w.moderatorKey === staffMember.key)) {
          const pending = this.moderationQueue.filter(item => 
                item.assignedModeratorKey === staffMember.key && 
            item.moderationStatus === 'pending'
          ).length;
          
          const completed = this.moderationQueue.filter(item => 
                item.assignedModeratorKey === staffMember.key && 
            (item.moderationStatus === 'approved' || item.moderationStatus === 'rejected')
          ).length;

          workload.push({
                moderatorKey: staffMember.key,
                moderator: staffMember,
            pending: pending,
            completed: completed,
            total: pending + completed
          });
            }
          }
        }

        this.moderatorWorkload = workload.sort((a, b) => b.total - a.total);
      } catch (error) {
        console.error('Error calculating moderator workload:', error);
        this.moderatorWorkload = [];
      }
    },

    // Enhanced fetch methods with moderator data
    async fetchModerationQueueEnhanced() {
      try {
        // Filter out empty values from filters
        const cleanFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => 
            value !== '' && value !== null && value !== undefined
          )
        );
        
        // Rename moderatedBy to moderatorKey for API compatibility
        const apiFilters = { ...cleanFilters };
        if (apiFilters.moderatedBy) {
          apiFilters.moderatorKey = apiFilters.moderatedBy;
          delete apiFilters.moderatedBy;
        }
        
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: this.pagination.currentPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...apiFilters,
            count: this.pagination.perPage,
            offset: (this.pagination.currentPage - 1) * this.pagination.perPage,
            includeModeratorInfo: true
          }
        });

        this.moderationQueue = response?.data || [];
        this.pagination.total = response?.total || 0;
        
        // Calculate stats from the fetched data
        this.calculateModerationStats();
        
        // Calculate moderator workload after fetching data
        await this.calculateModeratorWorkload();
        
        // Calculate content type statistics
        await this.calculateContentTypeStats();
        
        console.log('Fetched moderation queue:', this.moderationQueue.length);
      } catch (error) {
        console.error('Error fetching moderation queue:', error);
        this.moderationQueue = [];
        this.pagination.total = 0;
        
        // Handle specific error types
        if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
          this.notifyError(this.$t('backoffice.moderation.network-error') || 'Network error. Please check your connection and try again.');
        } else if (error.response?.status === 503) {
          this.notifyError(this.$t('backoffice.moderation.service-unavailable') || 'Moderation service is temporarily unavailable. Please try again later.');
        } else if (error.response?.status === 403) {
          this.notifyError(this.$t('backoffice.moderation.access-denied') || 'Access denied. You may not have permission to access moderation data.');
        } else {
          this.notifyError(this.$t('backoffice.moderation.fetch-error') || 'Error fetching moderation data. Please try again.');
        }
      }
    },

    // Content type statistics methods
    async calculateContentTypeStats() {
      try {
        const stats = [];
        // Only include currently implemented content types
        const contentTypes = [
          'questions', 'answers', 'posts', 'comments'
        ];
        // TODO: Add more content types when frontend implementation is ready
        // 'articles', 'blogs', 'challenges', 'chats', 'products', 'links',
        // 'organizations', 'communities', 'events', 'ideas', 'jobs', 'work_items',
        // 'tenders', 'media', 'entities', 'people', 'polls'

        for (const contentType of contentTypes) {
          const total = this.moderationQueue.filter(item => item.contentType === contentType).length;
          const pending = this.moderationQueue.filter(item => 
            item.contentType === contentType && item.moderationStatus === 'pending'
          ).length;

          if (total > 0) {
            stats.push({
              type: contentType,
              count: total,
              pending: pending,
              completed: total - pending
            });
          }
        }

        // Sort by count (descending)
        this.contentTypeStats = stats.sort((a, b) => b.count - a.count);
      } catch (error) {
        console.error('Error calculating content type stats:', error);
        this.contentTypeStats = [];
      }
    },

    filterByContentType(contentType) {
      this.filters.contentType = contentType;
      this.applyFilters();
    },

    getModeratorFeedback(item) {
      if (!item) return '';

      if (item.feedbackForUser && String(item.feedbackForUser).trim()) {
        return String(item.feedbackForUser).trim();
      }
      const mf = item.moderationFeedback || {};
      if (mf.comment && String(mf.comment).trim()) return String(mf.comment).trim();
      if (Array.isArray(mf.reasons) && mf.reasons.length) return mf.reasons.join('. ');
      // moderationCheck sometimes carries a JSON string with improveComment
      if (item.moderationCheck) {
        try {
          const parsed = typeof item.moderationCheck === 'string' ? JSON.parse(item.moderationCheck) : item.moderationCheck;
          if (parsed && parsed.improveComment && String(parsed.improveComment).trim()) {
            return String(parsed.improveComment).trim();
          }
        } catch (e) {
          // ignore parse errors
        }
      }
      return '';
    }
  }
};
</script>

<style lang="scss" scoped src="./scss/Dashboard.scss"></style>
